/**
 * Welcher gespeicherte Befund ist der Erstbefund, und welche duerfen
 * miteinander verglichen werden?
 *
 * Der Bestand ist nicht formel-homogen: mindestens vier Epochen zwischen Maerz
 * und Mai 2026, dazwischen eine Woche mit zwei parallel schreibenden Formeln.
 * Befunde ohne scoring_version stammen aus dieser Zeit und sind mit heutigen
 * Werten nicht vergleichbar — der v2-Commit beziffert selbst, dass ein
 * Rohprodukt allein durch die neue Rechnung von 70,00 auf 24,75 fiel.
 *
 * Sie werden deshalb weder als Basis genommen noch in den Verlauf gezeichnet.
 * Geloescht wird nichts; sie bleiben in der Datenbank.
 *
 * ---
 *
 * VERSIONSREINHEIT (Migration 20260802100000): "Zeilen mit unterschiedlicher
 * Version duerfen nicht gegeneinander verrechnet oder in derselben
 * Verlaufskurve gezeichnet werden."
 *
 * Ein Filter auf `scoring_version !== null` loest das NICHT. Er trennt nur alt
 * von neu und wirft beim naechsten Formelwechsel wieder zwei Metriken
 * zusammen: steigt die Version wie vorgeschrieben von v2.3 auf v2.4, liegen im
 * selben Feed eine v2.3-Zeile mit 60 und eine frische v2.4-Zeile mit 30. Ohne
 * Zielversion meldet der Kopf dem Nutzer minus 30 als ERGEBNIS SEINER
 * OPTIMIERUNG, und der Verlauf zeichnet beide Punkte in dieselbe Kurve — genau
 * der Fehler, gegen den die Spalte angelegt wurde.
 *
 * Die Zielversion kommt deshalb als PARAMETER herein, nicht aus einer im
 * Frontend gespiegelten Konstante. Eine Kopie von SCORING_VERSION hier waere
 * eine zweite Wahrheit, die still driftet: sie muesste bei jedem Edge-Deploy
 * von Hand nachgezogen werden, und tut man das nicht, filtert das Frontend
 * gegen eine Version, die niemand mehr schreibt — es bliebe kein einziger
 * Befund uebrig, ohne dass irgendetwas rot wird. Massgeblich ist immer, was in
 * der Zeile steht, die gerade den angezeigten Score stellt.
 *
 * Drei Faelle, absichtlich unterschieden:
 *   - Parameter WEGGELASSEN (undefined): altes Verhalten, jede bekannte Version
 *     zaehlt. Damit bleibt jeder Aufrufer lauffaehig, der die Zielversion noch
 *     nicht durchreicht.
 *   - Parameter `null`: der aktuelle Befund traegt selbst keine Version. Dann
 *     ist nichts mit ihm vergleichbar, und es gibt weder Basis noch Verlauf.
 *   - Parameter mit Wert: nur Zeilen genau dieser Version.
 */

export interface VersionedSnapshot {
  scoring_version: string | null;
  snapshot_type: string;
  created_at: string;
}

function byTimeAsc<T extends VersionedSnapshot>(a: T, b: T): number {
  return Date.parse(a.created_at) - Date.parse(b.created_at);
}

/**
 * Praedikat fuer "darf mit der Zielversion verrechnet werden".
 *
 * `undefined` unterscheidet sich hier bewusst von `null` — deshalb der
 * explizite Vergleich statt eines Falsy-Checks. Ein `?? `-Kurzschluss haette
 * beide Faelle zusammengeworfen und dem Aufrufer, der ehrlich `null` meldet,
 * das lockere Alt-Verhalten untergeschoben.
 */
function matchesVersion(version: string | null, target: string | null | undefined): boolean {
  if (target === undefined) return version !== null;
  if (target === null) return false;
  return version === target;
}

/**
 * Der aelteste Befund der Zielversion, oder null.
 *
 * @param version Version des aktuell angezeigten Befunds. Weglassen behaelt das
 *   alte Verhalten (jede bekannte Version).
 */
export function pickBaseline<T extends VersionedSnapshot>(
  snapshots: T[],
  version?: string | null,
): T | null {
  const known = snapshots.filter((s) => matchesVersion(s.scoring_version, version));
  if (known.length === 0) return null;
  return [...known].sort(byTimeAsc)[0];
}

/**
 * Der aelteste PRE-Befund der Zielversion, oder null.
 *
 * WOZU EINE ZWEITE BASIS: `pickBaseline` beantwortet "was war der Erstbefund
 * dieses Feeds" und ist damit die richtige Bezugsgroesse fuer die Note im Kopf
 * (`compareScore`) -- unabhaengig davon, ob dieser Erstbefund vor oder nach
 * einer Optimierung gemessen wurde.
 *
 * Die Frage "hat die Optimierung diese Baustelle angefasst" ist eine andere,
 * und sie hat eine engere Bedingung: `buildBreakdown` haelt nur einen
 * post-Befund gegen einen pre-Befund (`comparable` dort). Ist der erste
 * Befund der aktuellen Formel-Version ein POST -- der Normalfall auf einem
 * Bestandsfeed, weil die naechtliche Automatik vor dem ersten Feed-Check
 * scort --, liefert `pickBaseline` genau diesen post, `comparable` sagt false,
 * und die ganze Funktion bliebe auf dem Feed dauerhaft stumm, ohne dass
 * irgendetwas rot wird.
 *
 * Die Spec (2026-09-15, 2.3) sagt "dieselbe Wahl wie compareScore". Das
 * Final-Review hat gezeigt, dass das die Funktion auf Bestandsfeeds
 * abschaltet; die Abweichung ist ein Ruling des Controllers und steht im
 * Ledger.
 */
export function pickPreBaseline<T extends VersionedSnapshot & { snapshot_type?: string | null }>(
  snapshots: T[],
  version?: string | null,
): T | null {
  return pickBaseline(snapshots.filter((s) => s.snapshot_type === 'pre'), version);
}

/**
 * Alle Befunde der Zielversion ab der Basis, aufsteigend. Leer, wenn es keine
 * Basis gibt.
 */
export function comparableSnapshots<T extends VersionedSnapshot>(
  snapshots: T[],
  version?: string | null,
): T[] {
  const baseline = pickBaseline(snapshots, version);
  if (!baseline) return [];
  const from = Date.parse(baseline.created_at);
  return snapshots
    .filter((s) => matchesVersion(s.scoring_version, version) && Date.parse(s.created_at) >= from)
    .sort(byTimeAsc);
}
